/**
 * GA4 conversion tracking for Azulo Travel.
 *
 * Event names (mark these as Key Events in GA4 → Admin → Events):
 *   generate_lead        – enquiry form submitted (GA4 recommended event)
 *   whatsapp_click       – any wa.me link tapped (placement tells you which one)
 *   phone_click          – any tel: link tapped
 *   itinerary_generated  – AI planner produced an itinerary
 *   itinerary_download   – AI itinerary PDF downloaded
 *
 * All helpers are no-ops when gtag is unavailable (ad blockers, SSR, tests).
 */

const GA_ID = "G-Q5N54EK3EB";

const clean = (params) =>
  Object.fromEntries(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

export const track = (eventName, params = {}) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", eventName, { ...clean(params), send_to: GA_ID });
};

export const trackEnquiry = ({ source, destination, corporate, code, hasItinerary }) =>
  track("generate_lead", {
    lead_source: corporate ? "corporate" : source || "contact",
    destination,
    lead_code: code,
    with_itinerary: hasItinerary ? "yes" : "no",
    currency: "INR",
  });

export const trackWhatsAppClick = ({ placement, destination, page }) =>
  track("whatsapp_click", { placement, destination, page });

export const trackPhoneClick = ({ placement, page }) =>
  track("phone_click", { placement, page });

export const trackItineraryGenerated = ({ destination, days, travellers, budgetInr, tier }) =>
  track("itinerary_generated", {
    destination,
    duration_days: days,
    travellers,
    value: budgetInr,
    currency: "INR",
    tier,
  });

export const trackItineraryDownload = ({ planId, destination }) =>
  track("itinerary_download", { plan_id: planId, destination, file_extension: "pdf" });

/** Human-readable placement from the nearest data-testid, e.g. "footer-whatsapp" → "footer". */
const placementOf = (el) => {
  const tid = el.closest("[data-testid]")?.getAttribute("data-testid") || "";
  return tid.replace(/-?(whatsapp|phone|link|button|wrapper)/g, "").replace(/-+$/, "") || "unknown";
};

/**
 * One delegated listener catches every WhatsApp / phone link on the site, so new
 * buttons are tracked automatically without per-component wiring.
 * Returns an unsubscribe function.
 */
export const installContactLinkTracking = () => {
  const onClick = (event) => {
    const anchor = event.target.closest?.("a[href]");
    if (!anchor) return;
    const href = anchor.getAttribute("href") || "";
    const page = window.location.pathname;
    if (/wa\.me|api\.whatsapp\.com/.test(href)) {
      trackWhatsAppClick({
        placement: placementOf(anchor),
        destination: anchor.dataset.destination,
        page,
      });
    } else if (href.startsWith("tel:")) {
      trackPhoneClick({ placement: placementOf(anchor), page });
    }
  };
  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
};
